import { Link } from 'react-router';
import { useIVRConfig } from './hooks/useIVRConfig';
import { IVR_TEMPLATE_DISPLAY_NAMES, isActiveIvrTemplateId } from '@bariendo/lib/constants';
import { ReactElement, useMemo, useState } from 'react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import IVRHoursLanguageDrawer from './components/IVRHoursLanguageDrawer';
import { IconAlertCircle, IconCalendarEvent, IconSettings } from '@tabler/icons-react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Container,
  Group,
  Loader,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@mantine/core';

export function IVRConfigPage(): ReactElement {
  const {
    currentUserPermissions: { canConfigureIvr },
  } = useCurrentUser();

  // BAR-2441: gate on what the server actually permits. canConfigureIvr is true for
  // administrators (AdminAccessPolicy) and for users granted the opt-in IVR config
  // permission (IvrConfigAccessPolicy). It deliberately excludes
  // ProjectMembership.admin, which grants project administration but no FHIR data
  // access — admitting those users rendered a page that 403s on every request.
  const isAuthorized = canConfigureIvr;

  if (!isAuthorized) {
    return (
      <Container size="xl" py="md">
        <Alert icon={<IconAlertCircle size={16} />} title="Access Denied" color="red">
          This page is only accessible to administrators and users granted IVR configuration access.
        </Alert>
      </Container>
    );
  }

  return <AuthorizedIVRConfigPage />;
}

function AuthorizedIVRConfigPage(): ReactElement {
  const {
    loading,
    hoursSets,
    configuredHoursSetIds,
    menuSettings,
    saveHoursSet,
    saveMenuSettings,
    templateEntries,
    loadError,
  } = useIVRConfig();
  const [businessHoursDrawerOpen, setBusinessHoursDrawerOpen] = useState(false);

  const templates = useMemo(
    () =>
      [...templateEntries.values()]
        .map(({ parsed }) => parsed)
        // Off-hours templates are unreachable now that hours resolve per option, so listing
        // them offers an editor whose output no caller will ever hear.
        .filter((template) => isActiveIvrTemplateId(template.templateId))
        .sort((a, b) =>
          IVR_TEMPLATE_DISPLAY_NAMES[a.templateId].localeCompare(IVR_TEMPLATE_DISPLAY_NAMES[b.templateId])
        ),
    [templateEntries]
  );

  const usedDigits = useMemo(
    () =>
      new Set(
        [...templateEntries.values()].flatMap(({ parsed }) => parsed.keyMappings.map((mapping) => mapping.digit))
      ),
    [templateEntries]
  );

  return (
    <Container size="xl" py="md">
      <Group justify="space-between" mb="lg" align="center">
        <Title order={1}>IVR Configuration</Title>
        <Group gap="sm">
          <Button
            color="blue"
            radius="xl"
            variant="outline"
            component={Link}
            to="/on-call-schedule"
            leftSection={<IconCalendarEvent size={16} />}
          >
            On-Call NP Schedule
          </Button>
          <Button
            color="blue"
            radius="xl"
            variant="outline"
            leftSection={<IconSettings size={16} />}
            onClick={() => setBusinessHoursDrawerOpen(true)}
          >
            Hours & language
          </Button>
        </Group>
      </Group>

      <IVRHoursLanguageDrawer
        opened={businessHoursDrawerOpen}
        onClose={() => setBusinessHoursDrawerOpen(false)}
        loading={loading}
        hoursSets={hoursSets}
        configuredHoursSetIds={configuredHoursSetIds}
        menuSettings={menuSettings}
        usedDigits={usedDigits}
        onSaveHoursSet={saveHoursSet}
        onSaveMenuSettings={saveMenuSettings}
      />

      {loadError && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title={loadError.kind === 'permission' ? 'Access Denied' : 'Could not load IVR configuration'}
          color="red"
          mb="md"
        >
          {loadError.message}
        </Alert>
      )}

      {loading ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : templates.length === 0 ? (
        <Center py="xl">
          <Text c="dimmed">No IVR templates configured.</Text>
        </Center>
      ) : (
        <SimpleGrid cols={2} spacing="md">
          {templates.map((template) => {
            const configuredKeys = template.keyMappings.length;
            const hasAudio = !!template.audioFile;

            return (
              <Card key={template.templateId} withBorder padding="lg">
                <Group justify="space-between" align="flex-start" wrap="nowrap">
                  <Stack gap="xs">
                    {/* The stored description explains a business-hours/off-hours split that
                        no longer exists, and nothing in the UI can edit it. The badges below
                        already say what there is to say about each template. */}
                    <Text fw={600} size="md">
                      {IVR_TEMPLATE_DISPLAY_NAMES[template.templateId]}
                    </Text>
                  </Stack>
                  <Button
                    size="xs"
                    radius="xl"
                    color="blue"
                    variant="light"
                    component={Link}
                    to={`/ivr-configuration/${template.templateId}`}
                  >
                    Edit
                  </Button>
                </Group>

                <Group mt="md" gap="xs">
                  <Badge variant="outline" color={configuredKeys > 0 ? 'blue' : 'gray'} size="sm">
                    {configuredKeys}/10 keys configured
                  </Badge>
                  <Badge variant="outline" color={hasAudio ? 'green' : 'gray'} size="sm">
                    {hasAudio ? 'Audio set' : 'No audio'}
                  </Badge>
                </Group>
              </Card>
            );
          })}
        </SimpleGrid>
      )}
    </Container>
  );
}
